import { Controller, useFormContext } from 'react-hook-form';
import { DateTimeRangeTimezonePicker } from 'shared/mui/DatePicker/DateTimeRangeTimezonePicker/DateTimeRangeTimezonePicker';
import { FilterFieldConfig } from 'shared/mui/Filter/config';
import { useFilterContext } from 'shared/mui/Filter/context/filterContext';
import { useFilterTimezone } from 'shared/mui/Filter/hooks/useFilterTimezone';
import {
  AutocompleteInput,
  AutocompleteMultiselectInput,
  EnumAutocompleteInput,
} from 'shared/react-admin/Inputs';
import { ReferenceArrayInput, ReferenceInput } from 'shared/react-admin/ReferenceInputs';
import { Currency } from 'types/embedded/currency';

export const useOrdersReportFilterFields = (): FilterFieldConfig[] => {
  const { control, resetField, setValue, watch } = useFormContext();
  const { timezone, onChangeTimezone } = useFilterTimezone();
  const { setAppliedFilter, appliedFilters } = useFilterContext();
  const [company, merchant, provider, gateway, appliedDate, transactionsAppliedDate] = watch([
    'company',
    'merchant',
    'provider',
    'gateway',
    'appliedDate',
    'transactionsAppliedDate',
  ]);

  return [
    {
      name: 'appliedDate',
      label: 'Дата создания',
      chipType: 'dateTimeRangeTimezone',
      input: (
        <Controller
          control={control}
          name="appliedDate"
          render={({ field: { onChange, onBlur, value } }) => (
            <DateTimeRangeTimezonePicker
              disabled={!!transactionsAppliedDate[0] || !!transactionsAppliedDate[1]}
              label="Дата создания от-до"
              maxDate={new Date()}
              onBlur={onBlur}
              onChange={onChange}
              onChangeTimezone={onChangeTimezone}
              timezone={timezone}
              value={value}
            />
          )}
        />
      ),
    },
    {
      name: 'transactionsAppliedDate',
      label: 'Дата исполнения',
      chipType: 'dateTimeRangeTimezone',
      input: (
        <Controller
          control={control}
          name="transactionsAppliedDate"
          render={({ field: { onChange, onBlur, value } }) => (
            <DateTimeRangeTimezonePicker
              disabled={!!appliedDate[0] || !!appliedDate[1]}
              label="Дата исполнения от-до"
              maxDate={new Date()}
              onBlur={onBlur}
              onChange={onChange}
              onChangeTimezone={onChangeTimezone}
              timezone={timezone}
              value={value}
            />
          )}
        />
      ),
    },
    {
      name: 'company',
      label: 'Компания',
      chipType: 'autocomplete',
      chipProps: {
        resource: 'companies',
        onApply: () => {
          setAppliedFilter('merchant', null);
          setAppliedFilter('provider', null);
          setAppliedFilter('gateway', []);
          setAppliedFilter('merchantAccount', []);
          setAppliedFilter('method', []);

          resetField('merchant', { keepDirty: true });
          resetField('provider', { keepDirty: true });
          resetField('gateway', { keepDirty: true });
          resetField('merchantAccount', { keepDirty: true });
          resetField('method', { keepDirty: true });
        },
        onDelete: () => {
          resetField('merchant', { keepDirty: true });
          resetField('provider', { keepDirty: true });
          resetField('gateway', { keepDirty: true });
          resetField('merchantAccount', { keepDirty: true });
          resetField('method', { keepDirty: true });

          setAppliedFilter('merchant', null);
          setAppliedFilter('provider', null);
          setAppliedFilter('gateway', []);
          setAppliedFilter('merchantAccount', []);
          setAppliedFilter('method', []);
        },
        onReset: () => {
          setValue('merchant', appliedFilters?.['merchant']);
          setValue('provider', appliedFilters?.['provider']);
          setValue('gateway', appliedFilters?.['gateway']);
          setValue('merchantAccount', appliedFilters?.['merchantAccount']);
          setValue('method', appliedFilters?.['method']);

          setAppliedFilter('merchant', appliedFilters?.['merchant']);
          setAppliedFilter('provider', appliedFilters?.['provider']);
          setAppliedFilter('gateway', appliedFilters?.['gateway']);
          setAppliedFilter('merchantAccount', appliedFilters?.['merchantAccount']);
          setAppliedFilter('method', appliedFilters?.['method']);
        },
      },
      input: (variant) => (
        <ReferenceInput
          filter={{ exists: { 'merchants.merchantAccounts': true }, archive: false }}
          perPage={100}
          reference="companies"
          source="company"
        >
          <AutocompleteInput
            filterToQuery={(searchText: string) => ({ name: searchText })}
            fullWidth
            helperText={false}
            label="Компания"
            {...(variant === 'field' && {
              onChange: () => {
                setValue('merchant', null);
                setValue('provider', null);
                setValue('gateway', []);
                setValue('merchantAccount', []);
                setValue('method', []);
              },
            })}
            optionText="name"
            variant="outlined"
          />
        </ReferenceInput>
      ),
    },
    {
      name: 'merchant',
      label: 'Мерчант',
      chipType: 'autocomplete',
      chipProps: {
        resource: 'merchants',
        onApply: () => {
          setAppliedFilter('provider', null);
          setAppliedFilter('gateway', []);
          setAppliedFilter('merchantAccount', []);
          setAppliedFilter('method', []);

          resetField('provider', { keepDirty: true });
          resetField('gateway', { keepDirty: true });
          resetField('merchantAccount', { keepDirty: true });
          resetField('method', { keepDirty: true });
        },
        onDelete: () => {
          resetField('provider', { keepDirty: true });
          resetField('gateway', { keepDirty: true });
          resetField('merchantAccount', { keepDirty: true });
          resetField('method', { keepDirty: true });

          setAppliedFilter('provider', null);
          setAppliedFilter('gateway', []);
          setAppliedFilter('merchantAccount', []);
          setAppliedFilter('method', []);
        },
        onReset: () => {
          setValue('provider', appliedFilters?.['provider']);
          setValue('gateway', appliedFilters?.['gateway']);
          setValue('merchantAccount', appliedFilters?.['merchantAccount']);
          setValue('method', appliedFilters?.['method']);

          setAppliedFilter('provider', appliedFilters?.['provider']);
          setAppliedFilter('gateway', appliedFilters?.['gateway']);
          setAppliedFilter('merchantAccount', appliedFilters?.['merchantAccount']);
          setAppliedFilter('method', appliedFilters?.['method']);
        },
      },
      input: (variant) => (
        <ReferenceInput
          enableGetChoices={() => !!company}
          filter={{
            companies: company,
            exists: { merchantAccounts: true },
            archive: false,
          }}
          perPage={100}
          reference="merchants"
          source="merchant"
        >
          <AutocompleteInput
            {...(variant === 'field' && {
              disabled: !company,
              disabledHint: 'Сначала выберите компанию',
              onChange: () => {
                setValue('provider', null);
                setValue('gateway', []);
                setValue('merchantAccount', []);
                setValue('method', []);
              },
            })}
            filterToQuery={(searchText: string) => ({ name: searchText })}
            fullWidth
            helperText={false}
            label="Мерчант"
            optionText="name"
            variant="outlined"
          />
        </ReferenceInput>
      ),
    },
    {
      name: 'provider',
      label: 'Провайдер',
      chipType: 'autocomplete',
      chipProps: {
        resource: 'providers',
        onApply: () => {
          setAppliedFilter('gateway', []);
          setAppliedFilter('merchantAccount', []);
          setAppliedFilter('method', []);

          resetField('gateway', { keepDirty: true });
          resetField('merchantAccount', { keepDirty: true });
          resetField('method', { keepDirty: true });
        },
        onDelete: () => {
          resetField('gateway', { keepDirty: true });
          resetField('merchantAccount', { keepDirty: true });
          resetField('method', { keepDirty: true });

          setAppliedFilter('gateway', []);
          setAppliedFilter('merchantAccount', []);
          setAppliedFilter('method', []);
        },
        onReset: () => {
          setValue('gateway', appliedFilters?.['gateway']);
          setValue('merchantAccount', appliedFilters?.['merchantAccount']);
          setValue('method', appliedFilters?.['method']);

          setAppliedFilter('gateway', appliedFilters?.['gateway']);
          setAppliedFilter('merchantAccount', appliedFilters?.['merchantAccount']);
          setAppliedFilter('method', appliedFilters?.['method']);
        },
      },
      input: (variant) => (
        <ReferenceInput
          enableGetChoices={() => !!merchant}
          filter={{ merchantAccounts: { merchants: merchant } }}
          perPage={100}
          reference="providers"
          sort={{ field: 'name', order: 'ASC' }}
          source="provider"
        >
          <AutocompleteInput
            clearOnBlur={false}
            {...(variant === 'field' && {
              disabled: !merchant,
              disabledHint: 'Сначала выберите мерчанта',
              onChange: () => {
                setValue('merchantAccount', []);
                setValue('gateway', []);
                setValue('method', []);
              },
            })}
            filterToQuery={(searchText: string) => ({ name: searchText })}
            fullWidth
            helperText={false}
            label="Провайдер"
            optionText="name"
            variant="outlined"
          />
        </ReferenceInput>
      ),
    },
    {
      name: 'merchantAccount',
      label: 'Счета мерчантов',
      chipType: 'multiselect',
      chipProps: {
        resource: 'merchant_accounts',
        limitTags: 1,
      },
      input: (variant) => (
        <ReferenceArrayInput
          enableGetChoices={() => !!merchant && !!provider}
          filter={{
            merchants: merchant,
            provider,
          }}
          perPage={100}
          reference="merchant_accounts"
          source="merchantAccount"
        >
          <AutocompleteMultiselectInput
            {...(variant === 'field' && {
              disabled: !provider,
              disabledHint: 'Сначала выберите провайдера',
            })}
            filterToQuery={(searchText: string) => ({ name: searchText })}
            helperText={false}
            label="Счета мерчанта"
            name="merchantAccount"
            optionText="name"
          />
        </ReferenceArrayInput>
      ),
    },
    {
      name: 'gateway',
      label: 'Шлюзы',
      chipType: 'multiselect',
      chipProps: {
        resource: 'gateways',
        limitTags: 1,
        onApply: () => {
          setAppliedFilter('method', []);
          resetField('method', { keepDirty: true });
        },
        onDelete: () => {
          resetField('method', { keepDirty: true });
          setAppliedFilter('method', []);
        },
        onReset: () => {
          setValue('method', appliedFilters?.['method']);
          setAppliedFilter('method', appliedFilters?.['method']);
        },
      },
      input: (variant) => (
        <ReferenceArrayInput
          enableGetChoices={() => !!merchant && !!provider}
          filter={{
            merchantAccounts: { merchants: merchant },
            provider,
          }}
          perPage={100}
          reference="gateways"
          source="gateway"
        >
          <AutocompleteMultiselectInput
            {...(variant === 'field' && {
              disabled: !provider,
              disabledHint: 'Сначала выберите провайдера',
              onChange: () => {
                setValue('method', []);
              },
            })}
            filterToQuery={(searchText: string) => ({ name: searchText })}
            helperText={false}
            label="Шлюзы"
            name="gateway"
            optionText="name"
          />
        </ReferenceArrayInput>
      ),
    },
    {
      name: 'method',
      label: 'Методы',
      chipType: 'multiselect',
      chipProps: {
        resource: 'methods',
        limitTags: 1,
      },
      input: (variant) => (
        <ReferenceArrayInput
          filter={{
            gateway,
          }}
          perPage={100}
          reference="methods"
          source="method"
        >
          <AutocompleteMultiselectInput
            {...(variant === 'field' && {
              disabled: !gateway?.length,
              disabledHint: 'Сначала выберите шлюз',
            })}
            helperText={false}
            label="Методы"
            name="method"
            optionText="name"
          />
        </ReferenceArrayInput>
      ),
    },
    {
      name: 'direction',
      label: 'Направление',
      chipType: 'enumAutocomplete',
      chipProps: {
        resource: 'directions',
      },
      input: (
        <EnumAutocompleteInput
          filterToQuery={(searchText: string) => ({ description: searchText })}
          fullWidth
          helperText={false}
          label="Направление"
          name="direction"
          optionText="description"
          optionValue="value"
          resource="directions"
          source="direction"
          variant="outlined"
        />
      ),
    },
    {
      name: 'currency',
      label: 'Валюта',
      chipType: 'multiselect',
      chipProps: {
        resource: 'currencies',
        limitTags: 1,
      },
      input: (
        <ReferenceArrayInput
          filter={{ active: true }}
          perPage={50}
          reference="currencies"
          source="currency"
        >
          <AutocompleteMultiselectInput
            filterToQuery={(searchText: string) => ({ currency: searchText })}
            getOptionLabel={(record: Currency) => `${record?.alphaCode}, ${record?.name}`}
            helperText={false}
            label="Валюта"
            name="currency"
          />
        </ReferenceArrayInput>
      ),
    },
  ];
};
